import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUser } from "@/providers/user";
import { DataService } from "@/services/data";
import { Loader2 } from "lucide-react";

import { offerFormSchema, offerFormSchemaWithSeats } from "../forms/offer/_schema";
import { OfferCommonForm } from "../forms/offer/common";
import { OfferProAForm } from "../forms/offer/pro-a";

type CreateOpportunityResponse = {
    opportunity_id: number;
    offer_id: number | null;
    redirect_url?: string;
};

type AccountCreateOfferDialogProps = {
    recordId: string | number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
};

function buildDefaultValues(foundByCsm: boolean): z.infer<typeof offerFormSchema> {
    return {
        apprentissage: false,
        contrat_pro: false,
        pro_a: false,
        product: "",
        available_seats: 1,
        job_link: "",
        interview_slots_description: "",
        rqth: false,
        age_max_toggle: false,
        age_max: null,
        max_commute_toggle: false,
        max_commute: null,
        active: true,
        found_by_candidate: false,
        found_by_csm: foundByCsm,
        candidate: {
            first_name: "",
            last_name: "",
            email: "",
            phone: ""
        },
        current_job: {
            title: "",
            classification: "",
            level: "",
            hierarchical_coefficient: "",
            starting_date: ""
        }
    };
}

export function AccountCreateOfferDialog({ recordId, open, onOpenChange }: AccountCreateOfferDialogProps) {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const { user, has_any_group } = useUser();
    const is_admin = has_any_group(["app_apprenticeship_admin"]);
    const is_csm = has_any_group(["team_apprenticeship_csm"]);
    const is_cap = has_any_group(["team_apprenticeship_cap", "app_apprenticeship_admin"]);

    const resolver = useMemo(() => zodResolver(offerFormSchemaWithSeats), []);
    const form = useForm<z.infer<typeof offerFormSchema>>({
        mode: "onSubmit",
        resolver,
        defaultValues: buildDefaultValues(is_csm)
    });

    useEffect(() => {
        if (open) {
            form.reset(buildDefaultValues(is_csm));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only on open / CSM flag
    }, [open, is_csm]);

    const getActiveTab = () => (form.watch("pro_a") ? "pro_a" : "classic");

    const handleTabChange = (value: string) => {
        form.setValue("apprentissage", false);
        form.setValue("contrat_pro", false);
        form.setValue("pro_a", value === "pro_a");
    };

    const onSubmit = async (values: z.infer<typeof offerFormSchema>) => {
        setSubmitting(true);
        try {
            // Always create active (stage New). Activate/deactivate later via stage on the opportunity.
            const payload = {
                product: Number(values.product),
                available_seats: values.available_seats ?? 1,
                apprentissage: values.apprentissage,
                contrat_pro: values.contrat_pro,
                pro_a: values.pro_a,
                rqth: values.rqth,
                active: true,
                found_by_candidate: values.found_by_candidate,
                found_by_csm: values.found_by_csm,
                desired_starting_date: values.desired_starting_date || null,
                job_link: values.job_link || null,
                interview_slots_description: values.interview_slots_description || null,
                age_max: values.age_max_toggle ? values.age_max : null,
                max_commute: values.max_commute_toggle ? values.max_commute : null,
                ...(values.pro_a ? { candidate: values.candidate } : {}),
                ...(values.pro_a ? { current_job: values.current_job } : {})
            };

            const response = await DataService.client
                .url(`/crm/objects/accounts/${recordId}/create_apprenticeship_opportunity/`)
                .post(payload)
                .json<CreateOpportunityResponse>();

            toast.success("L'offre a bien été créée");
            onOpenChange(false);
            navigate(response.redirect_url || `/opportunities/${response.opportunity_id}/informations`);
        } catch (error: unknown) {
            const message =
                (error as { json?: { error?: string; detail?: string } })?.json?.error ||
                (error as { json?: { detail?: string } })?.json?.detail ||
                "Une erreur est survenue lors de la création de l'offre";
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[750px] max-h-[90vh] md:max-w-screen-lg overflow-auto">
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 outline-none">
                        <DialogHeader>
                            <DialogTitle>Offre</DialogTitle>
                            <DialogDescription>Créez une nouvelle offre pour un poste d'alternant</DialogDescription>
                        </DialogHeader>

                        <Tabs value={getActiveTab()} onValueChange={handleTabChange} className="space-y-6">
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="classic">Contrat classique</TabsTrigger>
                                <TabsTrigger value="pro_a">Contrat Pro-A</TabsTrigger>
                            </TabsList>

                            <TabsContent value="classic">
                                <OfferCommonForm
                                    form={form}
                                    is_cap={is_cap}
                                    is_admin={is_admin}
                                    user={user}
                                    showActiveToggle={false}
                                    showAvailableSeats
                                />
                            </TabsContent>
                            <TabsContent value="pro_a" className="space-y-4">
                                <OfferCommonForm
                                    form={form}
                                    is_cap={is_cap}
                                    is_admin={is_admin}
                                    user={user}
                                    showActiveToggle={false}
                                    showAvailableSeats
                                />
                                <OfferProAForm form={form} />
                            </TabsContent>
                        </Tabs>

                        <DialogFooter>
                            <Button type="button" onClick={form.handleSubmit(onSubmit)} disabled={submitting}>
                                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Créer l'offre
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
